import * as THREE from 'three';

/**
 * Collision world.
 *
 * The level is static and axis-aligned by construction, so we use AABB colliders in
 * a uniform spatial hash rather than a general triangle-mesh BVH. That buys exact,
 * jitter-free wall sliding and sub-microsecond broadphase queries, which matters far
 * more for an FPS feel than supporting arbitrary geometry.
 *
 * Actors (player, enemies) are capsules resolved by swept-AABB against the hash,
 * with axis-separated resolution so running into a corner never launches you.
 */

const CELL = 4; // metres per spatial-hash cell

export const MASK = {
  WORLD: 1 << 0,
  ACTOR: 1 << 1,
  PROP: 1 << 2,
  ALL: 0xffff,
};

let _colliderId = 0;

export class PhysicsWorld {
  constructor() {
    this.colliders = [];
    this.grid = new Map();
    this.actors = new Map();
    this._tmp = {
      v1: new THREE.Vector3(),
      v2: new THREE.Vector3(),
      box: new THREE.Box3(),
      mat: new THREE.Matrix4(),
    };
  }

  /* ------------------------------- building ------------------------------- */

  addBox(min, max, materialId = 'concrete', mask = MASK.WORLD) {
    const c = {
      id: _colliderId++,
      min: min.clone(),
      max: max.clone(),
      materialId,
      mask,
    };
    this.colliders.push(c);
    this._index(c);
    return c;
  }

  /**
   * Register a mesh's world-space bounding box as a collider. Meshes tagged
   * `userData.noCollide` are skipped; `userData.materialId` overrides the surface
   * type used for impact decals and audio.
   */
  addStatic(root) {
    root.updateWorldMatrix(true, true);
    root.traverse((o) => {
      if (!o.isMesh || o.userData.noCollide) return;
      if (!o.geometry.boundingBox) o.geometry.computeBoundingBox();
      const box = this._tmp.box.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
      const matId = o.userData.materialId ?? o.material?.userData?.materialId ?? 'concrete';
      this.addBox(box.min, box.max, matId, o.userData.collisionMask ?? MASK.WORLD);
    });
  }

  _index(c) {
    const x0 = Math.floor(c.min.x / CELL);
    const x1 = Math.floor(c.max.x / CELL);
    const z0 = Math.floor(c.min.z / CELL);
    const z1 = Math.floor(c.max.z / CELL);
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        const key = x * 73856093 ^ z * 19349663;
        let bucket = this.grid.get(key);
        if (!bucket) this.grid.set(key, (bucket = []));
        bucket.push(c);
      }
    }
  }

  /** Colliders whose cells overlap an XZ-projected box. Deduplicated. */
  _query(minX, minZ, maxX, maxZ, out) {
    out.length = 0;
    const seen = this._seen ?? (this._seen = new Set());
    seen.clear();
    const x0 = Math.floor(minX / CELL);
    const x1 = Math.floor(maxX / CELL);
    const z0 = Math.floor(minZ / CELL);
    const z1 = Math.floor(maxZ / CELL);
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        const bucket = this.grid.get(x * 73856093 ^ z * 19349663);
        if (!bucket) continue;
        for (const c of bucket) {
          if (seen.has(c.id)) continue;
          seen.add(c.id);
          out.push(c);
        }
      }
    }
    return out;
  }

  /* -------------------------------- actors -------------------------------- */

  registerActor(actor) {
    this.actors.set(actor.id, actor);
  }
  removeActor(id) {
    this.actors.delete(id);
  }

  /* ------------------------------- raycast -------------------------------- */

  /**
   * Ray vs world AABBs and actor hitboxes. Returns the nearest hit.
   * Actor hitboxes are oriented boxes; we transform the ray into their local space,
   * which is what makes headshot volumes feel precise instead of approximate.
   */
  raycast(origin, dir, maxDist = 1000, mask = MASK.ALL, ignoreActor = null) {
    let best = { hit: false, dist: maxDist, point: null, normal: null, materialId: null, actor: null, hitbox: null };

    if (mask & (MASK.WORLD | MASK.PROP)) {
      const candidates = this._raycastCandidates(origin, dir, maxDist);
      for (const c of candidates) {
        if (!(c.mask & mask)) continue;
        const r = rayAabb(origin, dir, c.min, c.max);
        if (r && r.t >= 0 && r.t < best.dist) {
          best = {
            hit: true,
            dist: r.t,
            point: origin.clone().addScaledVector(dir, r.t),
            normal: r.normal.clone(),
            materialId: c.materialId,
            actor: null,
            hitbox: null,
          };
        }
      }
    }

    if (mask & MASK.ACTOR) {
      for (const actor of this.actors.values()) {
        if (actor === ignoreActor || actor.dead) continue;
        // Cheap reject against the actor's bounding sphere first.
        const toActor = this._tmp.v1.subVectors(actor.position, origin);
        const along = toActor.dot(dir);
        if (along < -actor.boundingRadius || along > best.dist + actor.boundingRadius) continue;
        if (toActor.lengthSq() - along * along > actor.boundingRadius * actor.boundingRadius) continue;

        for (const hb of actor.hitboxes) {
          const r = rayObb(origin, dir, hb, actor);
          if (r && r.t >= 0 && r.t < best.dist) {
            best = {
              hit: true,
              dist: r.t,
              point: origin.clone().addScaledVector(dir, r.t),
              normal: r.normal,
              materialId: 'flesh',
              actor,
              hitbox: hb,
            };
          }
        }
      }
    }

    return best;
  }

  _raycastCandidates(origin, dir, maxDist) {
    const ex = origin.x + dir.x * maxDist;
    const ez = origin.z + dir.z * maxDist;
    return this._query(
      Math.min(origin.x, ex),
      Math.min(origin.z, ez),
      Math.max(origin.x, ex),
      Math.max(origin.z, ez),
      this._rayOut ?? (this._rayOut = [])
    );
  }

  /** Line-of-sight helper used by AI. */
  visible(from, to, ignoreActor = null) {
    const d = this._tmp.v1.subVectors(to, from);
    const dist = d.length();
    if (dist < 1e-4) return true;
    d.multiplyScalar(1 / dist);
    const hit = this.raycast(from, d, dist - 0.05, MASK.WORLD | MASK.PROP, ignoreActor);
    return !hit.hit;
  }

  /* ------------------------------ capsule sweep ---------------------------- */

  /**
   * Move a capsule by `delta`, resolving collisions axis-by-axis (Y, then X, then Z).
   * Returns the resolved position plus ground info. Axis separation is what gives
   * clean wall-slide: blocking X never cancels Z momentum.
   *
   * `pos` is the capsule's *feet* position.
   */
  sweepCapsule(pos, radius, height, delta, stepHeight = 0.45) {
    const out = {
      position: pos.clone(),
      grounded: false,
      groundNormal: new THREE.Vector3(0, 1, 0),
      groundMaterial: 'concrete',
      hitWall: false,
      wallNormal: new THREE.Vector3(),
      ceiling: false,
    };
    const p = out.position;

    const pad = Math.max(Math.abs(delta.x), Math.abs(delta.y), Math.abs(delta.z)) + radius + 0.5;
    const near = this._query(p.x - pad, p.z - pad, p.x + pad, p.z + pad, this._sweepOut ?? (this._sweepOut = []));
    const solids = near.filter((c) => c.mask & (MASK.WORLD | MASK.PROP));

    // --- vertical ---
    p.y += delta.y;
    for (const c of solids) {
      if (!overlapXZ(p, radius, c)) continue;
      const bottom = p.y;
      const top = p.y + height;
      if (top > c.min.y && bottom < c.max.y) {
        // The capsule overlaps this collider vertically. Report that as a ceiling
        // hit regardless of which way we were moving: callers probe headroom with
        // a zero delta (`_hasHeadroom`, `_tryMantle`), and the old code routed
        // `delta.y === 0` into the landing branch and never set `ceiling` at all,
        // which silently disabled both of those guards.
        if (bottom > c.min.y - height * 0.5) out.ceiling = true;

        if (delta.y < 0) {
          // Landing: snap to the surface only if we actually crossed it.
          if (bottom < c.max.y && bottom > c.max.y - Math.max(0.6, -delta.y * 2 + 0.1)) {
            p.y = c.max.y;
            out.grounded = true;
            out.groundMaterial = c.materialId;
            out.ceiling = false; // standing on it, not stuck under it
          }
        } else if (delta.y > 0 && top < c.min.y + Math.max(0.4, delta.y * 2 + 0.1)) {
          p.y = c.min.y - height;
          out.ceiling = true;
        }
      }
    }

    // --- horizontal, with step-up ---
    for (const axis of ['x', 'z']) {
      const d = delta[axis];
      if (d === 0) continue;
      p[axis] += d;
      for (const c of solids) {
        if (!overlapXZ(p, radius, c)) continue;
        if (p.y + height <= c.min.y || p.y >= c.max.y) continue;

        // Step up over low ledges instead of stopping dead.
        const stepUp = c.max.y - p.y;
        if (stepUp > 0 && stepUp <= stepHeight && this._canStand(p, radius, height, c.max.y, solids)) {
          p.y = c.max.y;
          out.grounded = true;
          out.groundMaterial = c.materialId;
          continue;
        }

        // Push out along the shallower penetration axis.
        const penNeg = p[axis] + radius - c.min[axis];
        const penPos = c.max[axis] - (p[axis] - radius);
        if (d > 0) {
          p[axis] = c.min[axis] - radius;
          out.wallNormal[axis] = -1;
        } else {
          p[axis] = c.max[axis] + radius;
          out.wallNormal[axis] = 1;
        }
        out.hitWall = true;
        void penNeg;
        void penPos;
      }
    }

    // --- ground probe (keeps you glued walking down slopes/stairs) ---
    if (!out.grounded && delta.y <= 0) {
      for (const c of solids) {
        if (!overlapXZ(p, radius, c)) continue;
        const gap = p.y - c.max.y;
        if (gap >= -0.02 && gap <= 0.12) {
          p.y = c.max.y;
          out.grounded = true;
          out.groundMaterial = c.materialId;
          break;
        }
      }
    }

    return out;
  }

  /** Is there headroom to stand at height y? Prevents stepping into a crawlspace. */
  _canStand(p, radius, height, y, solids) {
    for (const c of solids) {
      if (!overlapXZ(p, radius, c)) continue;
      if (y + height > c.min.y && y + 0.02 < c.max.y) return false;
    }
    return true;
  }

  /** Distance to the floor directly below a point, capped at maxDist. */
  groundHeight(x, z, fromY, maxDist = 50) {
    const near = this._query(x - 0.1, z - 0.1, x + 0.1, z + 0.1, this._gOut ?? (this._gOut = []));
    let best = fromY - maxDist;
    for (const c of near) {
      if (x < c.min.x || x > c.max.x || z < c.min.z || z > c.max.z) continue;
      if (c.max.y <= fromY + 0.01 && c.max.y > best) best = c.max.y;
    }
    return best;
  }
}

/* ------------------------------- primitives -------------------------------- */

function overlapXZ(p, radius, c) {
  return (
    p.x + radius > c.min.x && p.x - radius < c.max.x && p.z + radius > c.min.z && p.z - radius < c.max.z
  );
}

const _n = new THREE.Vector3();

/** Slab-method ray/AABB. Returns { t, normal } for the entry face. */
function rayAabb(o, d, min, max) {
  let tmin = -Infinity;
  let tmax = Infinity;
  let axis = 0;
  let sign = 1;
  for (let i = 0; i < 3; i++) {
    const k = i === 0 ? 'x' : i === 1 ? 'y' : 'z';
    const inv = 1 / (d[k] || 1e-12);
    let t1 = (min[k] - o[k]) * inv;
    let t2 = (max[k] - o[k]) * inv;
    let s = -1;
    if (t1 > t2) {
      const tmp = t1;
      t1 = t2;
      t2 = tmp;
      s = 1;
    }
    if (t1 > tmin) {
      tmin = t1;
      axis = i;
      sign = s;
    }
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return null;
  }
  if (tmax < 0) return null;
  _n.set(0, 0, 0);
  _n[axis === 0 ? 'x' : axis === 1 ? 'y' : 'z'] = sign;
  return { t: tmin < 0 ? 0 : tmin, normal: _n };
}

const _lo = new THREE.Vector3();
const _ld = new THREE.Vector3();
const _inv = new THREE.Matrix4();

/**
 * Ray vs oriented box. `hb` is { offset, halfExtents, tag, damageScale } expressed in
 * the actor's local space; the actor supplies a world matrix.
 */
function rayObb(origin, dir, hb, actor) {
  const m = hb.worldMatrix ?? actor.matrixWorld;
  if (!m) return null;
  _inv.copy(m).invert();
  _lo.copy(origin).applyMatrix4(_inv);
  _ld.copy(dir).transformDirection(_inv);
  const min = _tmpMin.copy(hb.offset).sub(hb.halfExtents);
  const max = _tmpMax.copy(hb.offset).add(hb.halfExtents);
  const r = rayAabb(_lo, _ld, min, max);
  if (!r) return null;
  return { t: r.t, normal: r.normal.clone().transformDirection(m) };
}

const _tmpMin = new THREE.Vector3();
const _tmpMax = new THREE.Vector3();
